import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCheckbox,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonNote,
  IonText,
} from "@ionic/react";
import { getFunctions, httpsCallable } from "firebase/functions";
import React, { useState } from "react";

interface GroupDetail {
  id: string;
  title: string;
  members: number;
  resolved: number;
}

interface BackfillResult {
  success: boolean;
  dryRun: boolean;
  songsScanned: number;
  songsUpdated: number;
  groupsScanned: number;
  groupsUpdated: number;
  groupDetails: GroupDetail[];
  submissionCounter: { current: number; next: number };
}

/**
 * ONE-TIME admin tool: runs backfillGroupPrivacy (functions/src/index.ts),
 * which stamps the scalar `groupId` mirror onto songs and resolves
 * `memberOwnerIds` on groups. Required for the group-scoped song feed
 * (SongService.groupFeedQuery) to return anything — see that function's
 * doc comment for the full explanation.
 *
 * Remove this tab (and the Cloud Function it calls) once the backfill has
 * been run against production.
 */
const GroupPrivacyBackfill: React.FC = () => {
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BackfillResult | null>(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const backfill = httpsCallable<{ dryRun: boolean }, BackfillResult>(
        getFunctions(),
        "backfillGroupPrivacy",
      );
      const res = await backfill({ dryRun });
      setResult(res.data);
    } catch (err: any) {
      setError(err?.message || "Backfill failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonCard>
      <IonCardHeader color="primary">
        <IonCardTitle>Group Privacy / Feed Backfill</IonCardTitle>
        <IonCardSubtitle>
          One-time: mirrors groups[0] onto the scalar groupId field the group
          feed query needs, and resolves each group's memberOwnerIds.
        </IonCardSubtitle>
      </IonCardHeader>

      <IonCardContent>
        <IonItem lines="none">
          <IonCheckbox
            slot="start"
            checked={dryRun}
            onIonChange={(e) => setDryRun(e.detail.checked)}
          />
          <IonLabel>Dry run (preview without writing)</IonLabel>
        </IonItem>

        <div style={{ marginTop: 16 }}>
          <IonButtons>
            <IonButton
              expand="block"
              color={dryRun ? "favorite" : "danger"}
              disabled={loading}
              onClick={run}
            >
              {dryRun ? "Run Dry Run" : "Run Backfill"}
            </IonButton>
          </IonButtons>
        </div>

        {error && (
          <IonItem color="danger" lines="none" style={{ marginTop: 12 }}>
            <IonLabel className="ion-text-wrap">
              <h3>Error</h3>
              <p>{error}</p>
            </IonLabel>
          </IonItem>
        )}

        {result && (
          <IonCard style={{ marginTop: 12 }}>
            <IonCardHeader color={result.dryRun ? "favorite" : "success"}>
              <IonCardTitle>
                {result.dryRun ? "Dry Run Result" : "Backfill Complete"}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonText>
                <p>
                  <strong>Songs scanned:</strong> {result.songsScanned}
                </p>
                <p>
                  <strong>
                    Songs {result.dryRun ? "that would be " : ""}updated:
                  </strong>{" "}
                  {result.songsUpdated}
                </p>
                <p>
                  <strong>Groups scanned:</strong> {result.groupsScanned}
                </p>
                <p>
                  <strong>
                    Groups {result.dryRun ? "that would be " : ""}updated:
                  </strong>{" "}
                  {result.groupsUpdated}
                </p>
                <p>
                  <strong>Submission counter:</strong>{" "}
                  {result.submissionCounter.current} →{" "}
                  {result.submissionCounter.next}
                </p>
              </IonText>

              <IonList>
                {result.groupDetails.map((g) => (
                  <IonItem key={g.id} lines="full">
                    <IonLabel className="ion-text-wrap">
                      <h3>{g.title}</h3>
                      <p>
                        {g.members} member(s) → {g.resolved} resolved owner
                        id(s)
                        {g.resolved < g.members &&
                          " — some members haven't signed in since the directory sync landed"}
                      </p>
                    </IonLabel>
                  </IonItem>
                ))}
              </IonList>

              <IonNote>
                {result.songsUpdated === 0 && result.groupsUpdated === 0
                  ? "Nothing left to backfill — the group feed should be populated now."
                  : result.dryRun
                    ? 'Uncheck "Dry run" and run again to write these updates.'
                    : "Done — refresh the songs feed to confirm."}
              </IonNote>
            </IonCardContent>
          </IonCard>
        )}

        <IonLoading isOpen={loading} message="Running backfill..." />
      </IonCardContent>
    </IonCard>
  );
};

export default GroupPrivacyBackfill;
